import type { FC, MouseEvent } from 'react'

import { Input, Radio, RadioGroup, Textarea, Select } from '@skytech/ui'
import { Button } from '@skytech/ui/Button'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { FieldProps } from 'formik'
import { Form, Field, Formik } from 'formik'
import * as yup from 'yup'

import { Translation } from '@/app/translations/Translation'
import { useTranslation } from '@/app/translations/translations-hooks'
import { useAdminAccess } from '@/hooks/access'
import queries from '@/modules/admin/queries'
import type { SalaryDeductionProfile } from '@/modules/admin/resources'
import adminResources from '@/modules/admin/resources'

import './TelePoliciesModal.styles.scss'

export const TelePoliciesModal: FC<{
  details: SalaryDeductionProfile | null
  list: SalaryDeductionProfile[]
  onClose: () => void
}> = ({ details, list, onClose }) => {
  useAdminAccess()

  const translate = useTranslation()
  const queryClient = useQueryClient()

  const translatedMandatoryMessage = translate('This field is mandatory')

  const validationSchema = yup.object({
    name: yup.string().required(translatedMandatoryMessage),
    telePolicyTemplateId: yup.number().required(translatedMandatoryMessage),
    freeAllowanceAmount: yup.number().required(translatedMandatoryMessage),
    comment: yup.string().required(translatedMandatoryMessage),
    global: yup.string().oneOf(['local', 'global']).required(translatedMandatoryMessage),
  })

  const { data: telePolicyTemplates, isLoading } = useQuery(
    queries.adminTelePolicyTemplatesList(),
    adminResources.getTelePolicyTemplatesList
  )

  const telePolicyTemplatesOptions = telePolicyTemplates?.map(tpt => ({
    value: tpt.id,
    label: tpt.name,
  }))

  const updateMutation = useMutation(adminResources.updateTelePolicy, {
    onSuccess(item) {
      const newList = list.map(d => {
        if (d.id === item.id) return item
        return d
      })

      queryClient.setQueryData(queries.adminTelePoliciesList(), [...newList])

      onClose()
    },
  })

  const createMutation = useMutation(adminResources.createTelePolicy, {
    onSuccess(item) {
      queryClient.setQueryData(queries.adminTelePoliciesList(), [...list, item])
      onClose()
    },
  })

  const deleteMutation = useMutation(adminResources.deleteTelePolicy, {
    onSuccess(id) {
      const newList = list.filter(d => d.id !== id)

      queryClient.setQueryData(queries.adminTelePoliciesList(), [...newList])

      onClose()
    },
  })

  const onDeleteHandler = (e: MouseEvent) => {
    e.preventDefault()
    deleteMutation.mutate(details!.id)
  }

  return (
    <Formik
      initialValues={{
        name: details?.name || '',
        telePolicyTemplateId: details?.telePolicyTemplateId,
        freeAllowanceAmount: details?.freeAllowanceAmount || 0,
        comment: details?.comment || '',
        global: details?.customerHead?.id ? 'global' : 'local',
      }}
      validationSchema={validationSchema}
      onSubmit={formValues => {
        const global = formValues.global === 'global'
        const telePolicyTemplateId = Number(formValues.telePolicyTemplateId)
        const freeAllowanceAmount = Number(formValues.freeAllowanceAmount)

        if (details?.id) {
          updateMutation.mutate({
            id: details.id,
            payload: { ...formValues, telePolicyTemplateId, global, freeAllowanceAmount },
          })
        } else {
          createMutation.mutate({
            payload: { ...formValues, telePolicyTemplateId, global, freeAllowanceAmount },
          })
        }
      }}
    >
      <div className="tele-policy-form">
        <h2>
          <Translation id={details ? 'Edit Tele Policy' : 'Add Tele Policy'} />
        </h2>

        <Form>
          <Field name="name">
            {({ field, meta }: FieldProps) => (
              <Input
                isFullWidth
                id="name"
                label={
                  <>
                    <Translation id="Name" />*
                  </>
                }
                marginBottom={16}
                error={meta.error}
                touched={meta.touched}
                {...field}
              />
            )}
          </Field>
          <Field name="telePolicyTemplateId">
            {({ field, form, meta }: FieldProps) => (
              <Select
                id="telePolicyTemplateId"
                label={
                  <>
                    <Translation id="Template" />*
                  </>
                }
                options={telePolicyTemplatesOptions || []}
                marginBottom={16}
                {...field}
                isLoading={isLoading}
                onChange={option => form.setFieldValue(field.name, option?.value)}
                error={meta.error}
                touched={meta.touched}
              />
            )}
          </Field>

          <Field name="freeAllowanceAmount">
            {({ field, meta }: FieldProps) => (
              <Input
                isFullWidth
                id="freeAllowanceAmount"
                label={
                  <>
                    <Translation id="Free Allowance Amount" />*
                  </>
                }
                marginBottom={16}
                error={meta.error}
                touched={meta.touched}
                type="number"
                {...field}
              />
            )}
          </Field>

          <Field name="comment">
            {({ field, meta }: FieldProps) => (
              <Textarea
                isFullWidth
                capitalizeLabel
                id="comment"
                label={<Translation id="Comment" />}
                marginBottom={16}
                error={meta.error}
                touched={meta.touched}
                {...field}
              />
            )}
          </Field>

          <RadioGroup
            id="type"
            label={
              <>
                <Translation id="Type" />*
              </>
            }
          >
            <Field name="global">
              {({ field }: FieldProps) => (
                <Radio
                  capitalizeLabel
                  id="global"
                  label={<Translation id="Global" />}
                  marginBottom={16}
                  {...field}
                />
              )}
            </Field>

            <Field name="global">
              {({ field }: FieldProps) => (
                <Radio
                  capitalizeLabel
                  id="local"
                  label={<Translation id="Local" />}
                  marginBottom={16}
                  {...field}
                />
              )}
            </Field>
          </RadioGroup>

          <footer>
            <Button
              variant="tertiary"
              disabled={createMutation.isLoading || updateMutation.isLoading}
              onClick={onClose}
            >
              <Translation id="Cancel" />
            </Button>

            {!!details?.id && (
              <Button
                variant="danger"
                onClick={onDeleteHandler}
                isLoading={createMutation.isLoading || updateMutation.isLoading}
              >
                <Translation id="Delete" />
              </Button>
            )}

            <Button
              variant="primary"
              type="submit"
              isLoading={createMutation.isLoading || updateMutation.isLoading}
            >
              <Translation id="Save" />
            </Button>
          </footer>
        </Form>
      </div>
    </Formik>
  )
}
